import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  monitoringAlertNotificationsSchema,
  monitoringAlertPageSchema,
  monitoringAlertSchema,
  monitoringCheckCreateSchema,
  monitoringCheckPageSchema,
  monitoringCheckResultPageSchema,
  monitoringCheckSchema,
  monitoringCheckUpdateSchema,
  monitoringNotificationPageSchema,
  monitoringWorkerStatusSchema,
  portalServerStatusSchema,
  testEmailResultSchema,
  type MonitoringAlert,
  type MonitoringCheck,
  type MonitoringCheckCreate,
  type MonitoringCheckUpdate,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';
import { serviceKeys } from '../services/api';

/**
 * Data access for the Admin Monitoring area — the existing Phase 04 endpoints only:
 *   GET   /api/monitoring/checks[?serviceId=&cursor=&limit=]   (monitoring.view)
 *   POST  /api/monitoring/checks                               (monitoring.manage)
 *   PATCH /api/monitoring/checks/:id  {target?, intervalSeconds?, enabled?} (monitoring.manage)
 *   GET   /api/monitoring/checks/:id/results[?cursor=&limit=]  (monitoring.view), newest first
 *   GET   /api/monitoring/alerts?state=open|resolved[&cursor=&limit=] (monitoring.view)
 *   GET   /api/monitoring/alerts/:id                           (monitoring.view)
 *   GET   /api/monitoring/worker                               (monitoring.view), worker liveness
 *   GET   /api/monitoring/notifications[?cursor=&limit=]       (monitoring.view), alert emails
 *   GET   /api/monitoring/alerts/:id/notifications             (monitoring.view)
 *   POST  /api/monitoring/notifications/test                   (monitoring.manage), test email
 * Alerts are read-only: the worker opens and resolves them; no request here changes one.
 * Status, staleness and every result value come from the API; nothing is computed here, and the
 * browser never contacts a monitored target itself.
 */
export const monitoringKeys = {
  all: ['monitoring'] as const,
  checks: (serviceId: string | null) => [...monitoringKeys.all, 'checks', serviceId] as const,
  check: (id: string) => [...monitoringKeys.all, 'check', id] as const,
  results: (id: string) => [...monitoringKeys.all, 'results', id] as const,
  alerts: (state: MonitoringAlert['state']) => [...monitoringKeys.all, 'alerts', state] as const,
  alert: (id: string) => [...monitoringKeys.all, 'alert', id] as const,
  worker: () => [...monitoringKeys.all, 'worker'] as const,
  portalServer: () => [...monitoringKeys.all, 'portal-server'] as const,
  notifications: () => [...monitoringKeys.all, 'notifications'] as const,
  notificationState: () => [...monitoringKeys.all, 'notifications', 'state'] as const,
  alertNotifications: (id: string) => [...monitoringKeys.all, 'alert-notifications', id] as const,
};

const PAGE_SIZE = 50;

export function useChecks(serviceId: string | null) {
  return useInfiniteQuery({
    queryKey: monitoringKeys.checks(serviceId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (serviceId) params.set('serviceId', serviceId);
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/monitoring/checks?${params}`, { schema: monitoringCheckPageSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

/**
 * One check. The API has no single-check endpoint, so the check list is walked (bounded) until the
 * check is found; null when it does not exist.
 */
export function useCheck(id: string) {
  return useQuery({
    queryKey: monitoringKeys.check(id),
    queryFn: async (): Promise<MonitoringCheck | null> => {
      let cursor: string | null = null;
      for (let page = 0; page < 20; page += 1) {
        const params = new URLSearchParams({ limit: '100' });
        if (cursor) params.set('cursor', cursor);
        const result = await apiRequest(`/monitoring/checks?${params}`, {
          schema: monitoringCheckPageSchema,
        });
        const found = result.items.find((c) => c.id === id);
        if (found) return found;
        cursor = result.nextCursor;
        if (!cursor) break;
      }
      return null;
    },
  });
}

export function useCheckResults(id: string) {
  return useInfiniteQuery({
    queryKey: monitoringKeys.results(id),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' });
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/monitoring/checks/${id}/results?${params}`, {
        schema: monitoringCheckResultPageSchema,
      });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** After any change: re-read monitoring data and the affected services' derived health. */
async function refreshAfterChange(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: monitoringKeys.all }),
    queryClient.invalidateQueries({ queryKey: serviceKeys.all }),
  ]);
}

export function useCreateCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MonitoringCheckCreate) =>
      apiRequest('/monitoring/checks', {
        method: 'POST',
        body: monitoringCheckCreateSchema.parse(input),
        schema: monitoringCheckSchema,
      }),
    onSuccess: async (check) => {
      queryClient.setQueryData(monitoringKeys.check(check.id), check);
      await refreshAfterChange(queryClient);
    },
  });
}

/** PATCH a check. The API decides what the change does (a new target or re-enabling clears the last result). */
export function useUpdateCheck(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: MonitoringCheckUpdate) =>
      apiRequest(`/monitoring/checks/${id}`, {
        method: 'PATCH',
        body: monitoringCheckUpdateSchema.parse(patch),
        schema: monitoringCheckSchema,
      }),
    onSuccess: async () => {
      await refreshAfterChange(queryClient);
    },
  });
}

/** Open or resolved alerts, newest first (opened / resolved), page by page. */
export function useAlerts(state: MonitoringAlert['state'], options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: monitoringKeys.alerts(state),
    enabled: options.enabled ?? true,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ state, limit: String(PAGE_SIZE) });
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/monitoring/alerts?${params}`, { schema: monitoringAlertPageSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** One alert; null when it does not exist. */
export function useAlert(id: string) {
  return useQuery({
    queryKey: monitoringKeys.alert(id),
    queryFn: async (): Promise<MonitoringAlert | null> => {
      try {
        return await apiRequest(`/monitoring/alerts/${id}`, { schema: monitoringAlertSchema });
      } catch (error) {
        if ((error as { status?: number }).status === 404) return null;
        throw error;
      }
    },
  });
}

/**
 * The monitoring worker's liveness, from its heartbeat (Phase 07 Stage 03). Never polled on a
 * timer — that would keep an idle session alive on the server. It is re-read whenever a page that
 * shows it is opened, and on request ("Check again").
 */
export function useWorkerStatus(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: monitoringKeys.worker(),
    enabled: options.enabled ?? true,
    queryFn: () => apiRequest('/monitoring/worker', { schema: monitoringWorkerStatusSchema }),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * The Portal Server's own state, read live by the API (Admin). Like the worker status: re-read
 * when the page opens and on request, never on a timer.
 */
export function usePortalServer() {
  return useQuery({
    queryKey: monitoringKeys.portalServer(),
    queryFn: () => apiRequest('/monitoring/portal-server', { schema: portalServerStatusSchema }),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Alert email deliveries, newest first, with whether alert emails are set up and failing (Phase
 * 07 Stage 05-C). Every value is the API's; no address or SMTP setting is ever in the response.
 */
export function useNotifications() {
  return useInfiniteQuery({
    queryKey: monitoringKeys.notifications(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '20' });
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/monitoring/notifications?${params}`, {
        schema: monitoringNotificationPageSchema,
      });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Only whether alert emails are set up and failing (the Admin Dashboard): one delivery read. */
export function useNotificationState(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: monitoringKeys.notificationState(),
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const page = await apiRequest('/monitoring/notifications?limit=1', {
        schema: monitoringNotificationPageSchema,
      });
      return { emailSetUp: page.emailSetUp, failing: page.failing };
    },
  });
}

/** One alert's alert and recovery emails, oldest first. */
export function useAlertNotifications(id: string) {
  return useQuery({
    queryKey: monitoringKeys.alertNotifications(id),
    queryFn: () =>
      apiRequest(`/monitoring/alerts/${id}/notifications`, {
        schema: monitoringAlertNotificationsSchema,
      }),
  });
}

/**
 * The Admin's test email, to their own address only (the API decides; the request names no
 * recipient). Success means the mail server accepted it, not that it reached the inbox.
 */
export function useSendTestEmail() {
  return useMutation({
    mutationFn: () =>
      apiRequest('/monitoring/notifications/test', {
        method: 'POST',
        body: {},
        schema: testEmailResultSchema,
      }),
  });
}
